import type { SceneNode } from "../scene/model";
import { invertAffine, type AffineMatrix } from "../geometry/affine";
import { multiply, nodeMatrix } from "../native/transforms";

const equal = (a: unknown, b: unknown) =>
  a === b || JSON.stringify(a) === JSON.stringify(b);
const near = (a: number, b: number) =>
  Math.abs(a - b) <= 1e-8 * Math.max(1, Math.abs(a), Math.abs(b));

/** Reproject the original sharp pixels; never resample one gesture preview into another.
 * Small dimension drift from transform decomposition is not a resize. */
function rigidMotion(before: SceneNode, after: SceneNode): AffineMatrix | null {
  const { width: aw, height: ah, ...ag } = before.geometry,
    { width: bw, height: bh, ...bg } = after.geometry;
  if (
    before.id !== after.id ||
    before.kind !== after.kind ||
    !near(aw!, bw!) ||
    !near(ah!, bh!) ||
    !equal(ag, bg) ||
    !equal(before.text, after.text) ||
    !equal(before.media, after.media) ||
    !equal(before.interaction, after.interaction) ||
    !!before.layer.clipToBelow !== !!after.layer.clipToBelow
  )
    return null;
  const inverse = invertAffine(nodeMatrix(before));
  if (!inverse) return null;
  const m = multiply(nodeMatrix(after), inverse);
  return m.every(Number.isFinite) &&
    near(m[0] * m[0] + m[1] * m[1], 1) &&
    near(m[2] * m[2] + m[3] * m[3], 1) &&
    near(m[0] * m[2] + m[1] * m[3], 0) &&
    near(m[0] * m[3] - m[1] * m[2], 1)
    ? m
    : null;
}

/** A clipped composite may follow a common motion only; moving inside a fixed mask
 * changes the visible content and must be painted from the actual sources. */
export function rasterMotion(
  before: SceneNode,
  after: SceneNode,
  beforeBase?: SceneNode,
  afterBase?: SceneNode,
): AffineMatrix | null {
  const motion = rigidMotion(before, after);
  if (!motion) return null;
  if (!beforeBase && !afterBase)
    return before.layer.clipToBelow ? null : motion;
  if (!beforeBase || !afterBase) return null;
  const baseMotion = rigidMotion(beforeBase, afterBase);
  return baseMotion && motion.every((v, i) => near(v, baseMotion[i]!))
    ? motion
    : null;
}
